"use client";
import {InlineFilter} from "@/components/@filter/inline-filter";
import {NeoDatePicker} from "@/components/forms";
import {NeoHeader} from "@/components/shared";
import {LoadingOverlay} from "@/components/table/neo-table/loaderNggrid";
import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {useNeoContext} from "@/context/neo/neo.context";
import {convertMTOKM, formatDateCommon} from "@/libs/helper";
import {reortsService} from "@/service/reports.service";
import dynamic from "next/dynamic";
import React, {useEffect, useMemo, useState} from "react";
import toast from "react-hot-toast";
import "./ChartTooltip.css";
const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

function fmtMonthDay(dateStr: string) {
  const d = new Date(dateStr);
  const m = MONTHS[d.getMonth()];
  const day = String(d.getDate()).padStart(2, "0");
  return `${m} ${day}`;
}

function fmtXAxis(dateStr: string) {
  const d = new Date(dateStr);
  const day = d.getDate();
  const mon = MONTHS[d.getMonth()];
  return `${day}\n${mon}`;
}

export const FuelConsumption: React.FC<any> = ({reportdata}) => {
  const [loadingContext, setLoadingContext] = useState<boolean>(true);
  const defaultfuelsummary = {
    filled: 0,
    consumed: 0,
    totalDistance: 0,
    fueltheft: 0
  };
  const [dataKey, setDataKey] = useState(0);
  const [chartKey, setChartKey] = useState(0);
  const {vehicles} = useNeoContext();
  const [fueltheft, setFuelTheft] = useState(false);
  const [reportdataState, setReportData] = useState(reportdata);
  const [fuelsummary, setFueSummary] = useState(defaultfuelsummary);
  const [selecteGroupId, setGroupId] = useState<string>("");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    from: null,
    to: null
  });

  const baseTableConfig = useMemo(
    () => ({
      title: reportdata?.activeReport?.menu
        ? reportdata?.activeReport?.menu
        : "Report",
      table: {
        columns: reportdata?.activeReport?.columns,
        activeColumns: reportdata?.activeReport?.activeColumns,
        activeFilters: reportdata?.activeReport?.filters,
        style: {
          height: "h-full"
        },
        meta: {
          id: 4,
          title: "",
          subHeader: reportdata.activeReport.subHeader,
          description: "Total Data",
          hasSearch: false,
          hasUpload: false,
          hasDownload: true,
          hasAdd: false,
          hasActions: false,
          hasCheckbox: false,
          hasFilter: false,
          hasFooter: true,
          hasHeader: true,
          hasPagination: true,
          hasCreate: false
        },
        url: {
          page: {},
          api: {
            get: reportdata?.activeReport?.url
          }
        },
        sidebar: reportdata?.activeReport?.sideBar,
        headerSidebar: reportdata?.activeReport?.headerSidebar,
        actions: [],
        innerFilters: reportdata?.activeReport?.innerFilters,
        tabledatas: []
      }
    }),
    [reportdata]
  );
  const [tableConfigData, setTableConfigData] = useState<any>(baseTableConfig);

  function formatYYYYMMDD(d: any) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  const categories = Array.from({length: 7}, (_, i) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (6 - i)); // 6 days ago ... today
    return formatYYYYMMDD(d);
  });
  const [chartcategories, setChartCategories] = useState(categories);

  const series = [
    {
      name: "Fuel Filling",
      data: [0, 0, 0, 0, 0, 0, 0]
    },
    {name: "Theft", data: [0, 0, 0, 0, 0, 0, 0]}
  ];
  const [chartseries, setChartSeries] = useState(series);

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: {show: false},
      parentHeightOffset: 0
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        borderRadius: 5,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last"
      }
    },
    stroke: {width: 0},
    dataLabels: {enabled: false},
    colors: ["#349C42", "#FF3131"],

    xaxis: {
      categories,
      labels: {
        formatter: val => fmtXAxis(String(val)),
        style: {colors: "#6B7280", fontSize: "12px"}
      },
      axisBorder: {show: false},
      axisTicks: {show: false}
    },

    yaxis: {
      title: {text: "Fuel Value (Litre)"},
      labels: {formatter: v => `${Math.round(v)} L`}
    },

    legend: {
      position: "bottom",
      horizontalAlign: "center"
      // markers: { width: 10, height: 4, radius: 2 }
    },

    tooltip: {
      shared: false,
      intersect: true,
      custom: ({series, seriesIndex, dataPointIndex, w}) => {
        const dateStr = String(
          w?.config?.xaxis?.categories?.[dataPointIndex] ?? ""
        );
        const value = series?.[seriesIndex]?.[dataPointIndex] ?? 0;
        // if you always want ↑ like screenshot:
        const arrow = "↑";
        return `
          <div style="background:#111827;color:#fff;padding:10px 12px;border-radius:8px;min-width:140px">
            <div style="font-weight:600;text-align:center">${fmtMonthDay(dateStr)}</div>
            <div style="margin-top:6px;display:flex;gap:6px;align-items:center;justify-content:center;font-size:12px">
              <span style="color:#F87171;font-weight:700">${arrow}</span>
              <span>${value} Litre</span>
            </div>
          </div>
        `;
      }
    }
  };
  const [chartoption, setOptions] = useState(options);

  useEffect(() => {
    const today = new Date();
    setSelectedDateRange({from: today, to: today});
  }, []);

  useEffect(() => {
    setOptions(prev => ({
      ...prev,
      xaxis: {
        ...prev.xaxis,
        categories: chartcategories
      }
    }));
  }, [chartcategories]);

  const onDateChange = (event: any) => {
    setSelectedDateRange(event);
  };

  useEffect(() => {
    fetchFuelAnalytics();
  }, [selectedDateRange]);

  const fetchFuelAnalytics = async () => {
    if (!selectedDateRange.from || !selectedDateRange.to) return;
    try {
      setLoadingContext(true);

      const fromTs = new Date(selectedDateRange.from).setHours(0, 0, 0, 0);
      const toTs = new Date(selectedDateRange.to).setHours(23, 59, 59, 999);

      const body = {
        from: fromTs,
        to: toTs
      };

      // 🔥 Call both APIs together
      const [summaryRes] = await Promise.all([
        reortsService.onGetFuelSummary(body),
        fueldatas() // second API
      ]);
      if (summaryRes.data.code === 200) {
        const responsedata = summaryRes?.data?.data;
        const fuelSummaryData = responsedata?.statistics?.summary || {};

        setFueSummary({
          filled: fuelSummaryData.filled || 0,
          consumed: fuelSummaryData.consumed || 0,
          totalDistance: parseFloat(
            convertMTOKM(fuelSummaryData.totalDistance || 0)
          ),
          fueltheft: fuelSummaryData.theft || 0
        });

        setFuelTheft(fuelSummaryData.theft > 0);

        const trends = responsedata?.statistics?.trends || [];

        const dailyMap: any = new Map<
          number,
          {fuelFill: number; fuelTheft: number}
        >();

        for (const day of trends) {
          const sum = (day.trendsData ?? []).reduce(
            (acc: any, p: any) => {
              acc.fuelFill += Number(p.fuelFill ?? 0);
              acc.fuelTheft += Number(p.fuelTheft ?? 0);
              return acc;
            },
            {fuelFill: 0, fuelTheft: 0}
          );

          const prev = dailyMap.get(day.date) ?? {
            fuelFill: 0,
            fuelTheft: 0
          };

          dailyMap.set(day.date, {
            fuelFill: prev.fuelFill + sum.fuelFill,
            fuelTheft: prev.fuelTheft + sum.fuelTheft
          });
        }

        const daily = [...dailyMap.entries()]
          .map(([date, v]) => ({date, ...v}))
          .sort((a, b) => a.date - b.date);

        setChartCategories(daily.map(d => formatDateCommon(d.date)));

        setChartSeries([
          {
            name: "Fuel Filling",
            data: daily.map(d => +d.fuelFill.toFixed(2))
          },
          {
            name: "Theft",
            data: daily.map(d => +d.fuelTheft.toFixed(2))
          }
        ]);

        setChartKey(j => j + 1);
      } else {
        toast.error(summaryRes.data.message);
      }
    } catch (err) {
      toast.error("Error fetching fuel report data");
    } finally {
      setLoadingContext(false);
    }
  };

  useEffect(() => {
    if (selectedDateRange.from && selectedDateRange.to) {
      fueldatas();
    }
  }, [selecteGroupId]);

  const fueldatas = async () => {
    try {
      setLoadingContext(true);

      let apiurl = "";
      if (selecteGroupId) {
        if (selecteGroupId === "1") apiurl = "/api/reports/fuel/vehicleGrouped";
        else if (selecteGroupId === "2")
          apiurl = "/api/reports/fuel/dateGrouped";
      } else {
        apiurl = tableConfigData?.table?.url?.api?.get;
      }

      const fromTs = selectedDateRange.from
        ? new Date(selectedDateRange.from).setHours(0, 0, 0, 0)
        : null;

      const toTs = selectedDateRange.to
        ? new Date(selectedDateRange.to).setHours(23, 59, 59, 999)
        : null;

      const body = {from: fromTs, to: toTs};

      const res: any = await reortsService.onGetFuelReportData(apiurl, body);

      if (res?.data?.code === 200) {
        const responsedata = res?.data?.data;
        const uids = (responsedata.columns ?? []).map((c: any) => c.uid);

        const tableData = (responsedata?.reportData ?? []).map((row: any) => {
          const d = Number(row?.distance ?? 0);
          const m = Number(row?.mileage ?? 0);

          const vehicleName = row.vehicle ?? row.vehicleName ?? "";

          return {
            ...row,
            vehicleName,
            distance: Number.isFinite(d) ? parseFloat(convertMTOKM(d)) : 0,
            mileage: Number.isFinite(m) ? parseFloat(convertMTOKM(m)) : 0
          };
        });

        setTableConfigData((prev: any) => ({
          ...prev,
          table: {
            ...prev.table,
            columns: responsedata.columns,
            activeColumns: uids,
            tabledatas: Array.isArray(tableData) ? tableData : []
          }
        }));

        setDataKey(k => k + 1);
      } else {
        toast.error(res?.data?.message || "Failed to fetch fuel report data");
      }
    } catch (err) {
      toast.error("Error fetching fuel report data");
    } finally {
      setLoadingContext(false);
    }
  };

  const handleGroupChange = (value: string) => {
    setGroupId(value);
  };

  return (
    <div
      className={
        loadingContext
          ? "relative"
          : "flex min-h-[calc(100vh-63px)] w-full flex-col p-5"
      }
    >
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
        <div className="mb-2 flex justify-between">
          <NeoHeader message="Fuel Consumption" />
          <div className="flex items-center justify-between gap-2">
            {/*<InlineFilter 
              filtertype={2} 
              vehicles={vehicles} 
              defaultVehicleKey={defaultvehicleKey} 
              onVehicleChange = {onVehicleChange} 
            />*/}
            <NeoDatePicker
              type={"range"}
              label=""
              handleDateChange={onDateChange}
              rangedefaultValue={selectedDateRange}
            />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-4 grid grid-cols-2 gap-2">
            <div className="m rounded-lg border p-3 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-full bg-[#FFC72733]/20 p-2">
                  <svg
                    width="15"
                    height="18"
                    viewBox="0 0 15 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.38574 0.366211C7.89746 -0.12207 7.10449 -0.12207 6.61621 0.366211L0.366211 6.61621C-0.12207 7.10449 -0.12207 7.89746 0.366211 8.38574C0.854492 8.87402 1.64746 8.87402 2.13574 8.38574L6.25293 4.26465V16.249C6.25293 16.9404 6.81152 17.499 7.50293 17.499C8.19434 17.499 8.75293 16.9404 8.75293 16.249V4.26465L12.8701 8.38184C13.3584 8.87012 14.1514 8.87012 14.6396 8.38184C15.1279 7.89355 15.1279 7.10059 14.6396 6.6123L8.38965 0.362305L8.38574 0.366211Z"
                      fill="#FFC727"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Fuel Filling
                </span>
              </div>
              <p className="text-xl font-semibold text-gray-900">
                <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                  {fuelsummary.filled} L
                </span>
              </p>
            </div>
            {fueltheft ? (
              <div className="rounded-xl border border-red-300 bg-red-50 p-2 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex justify-start p-1">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="46"
                        height="46"
                        rx="23"
                        fill="white"
                        fill-opacity="0.6"
                      />
                      <rect
                        x="1"
                        y="1"
                        width="46"
                        height="46"
                        rx="23"
                        stroke="#FECACA"
                        stroke-width="2"
                      />
                      <path
                        d="M24.0002 13.5C24.6658 13.5 25.2799 13.8516 25.6174 14.4281L35.7424 31.6781C36.0846 32.2594 36.0846 32.9766 35.7518 33.5578C35.419 34.1391 34.7955 34.5 34.1252 34.5H13.8752C13.2049 34.5 12.5815 34.1391 12.2487 33.5578C11.9158 32.9766 11.9205 32.2547 12.258 31.6781L22.383 14.4281C22.7205 13.8516 23.3346 13.5 24.0002 13.5ZM24.0002 19.5C23.3768 19.5 22.8752 20.0016 22.8752 20.625V25.875C22.8752 26.4984 23.3768 27 24.0002 27Cnan nan 25.1252 26.4984 25.1252 25.875V20.625C25.1252 20.0016 24.6237 19.5 24.0002 19.5ZM25.5002 30C25.5002 29.1721 24.8281 28.5 24.0002 28.5C23.1724 28.5 22.5002 29.1721 22.5002 30C22.5002 30.8279 23.1724 31.5 24.0002 31.5C24.8281 31.5 25.5002 30.8279 25.5002 30Z"
                        fill="#B91C1C"
                      />
                    </svg>
                  </div>
                  <div className="justify-center">
                    <p className="mt-2 text-sm font-semibold text-[#6B7280]">
                      Fuel Theft Alert
                    </p>
                    <div className="justify-center rounded-full bg-[#FEE2E2] p-1 text-center text-xs text-[#991B1B]">
                      <div className="flex items-center gap-1">
                        <div className="flex justify-center">
                          <svg
                            width="6"
                            height="6"
                            viewBox="0 0 6 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0 3C0 4.65575 1.34425 6 3 6C4.65575 6 6 4.65575 6 3C6 1.34425 4.65575 0 3 0C1.34425 0 0 1.34425 0 3Z"
                              fill="#EF4444"
                            />
                          </svg>
                        </div>
                        <p>Action Required</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-1 justify-center text-center">
                  <p className="text-3xl font-extrabold text-red-700">
                    {fuelsummary.fueltheft}
                  </p>
                </div>
                <div className="text-sm font-medium text-slate-500">
                  Abnormal fuel drops detected
                </div>
              </div>
            ) : (
              <div className="item-center rounded-lg border  p-2 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="46"
                      height="46"
                      rx="23"
                      fill="white"
                      fill-opacity="0.6"
                    />
                    <rect
                      x="1"
                      y="1"
                      width="46"
                      height="46"
                      rx="23"
                      stroke="#BBF7D0"
                      stroke-width="2"
                    />
                    <path
                      d="M24 12C24.2156 12 24.4313 12.0469 24.6281 12.1359L33.4547 15.8812C34.486 16.3172 35.2547 17.3344 35.25 18.5625C35.2266 23.2125 33.3141 31.7203 25.2375 35.5875C24.4547 35.9625 23.5453 35.9625 22.7625 35.5875C14.686 31.7203 12.7735 23.2125 12.75 18.5625C12.7453 17.3344 13.5141 16.3172 14.5453 15.8812L23.3766 12.1359C23.5688 12.0469 23.7844 12 24 12ZM24 15.1312V32.85C30.4688 29.7188 32.2078 22.7859 32.25 18.6281L24 15.1312Z"
                      fill="#15803D"
                    />
                  </svg>
                  <div>
                    <p className="mt-2 text-sm font-semibold text-[#6B7280]">
                      Possible Fuel Theft
                    </p>
                  </div>
                </div>
                <p className="text-xl font-semibold text-gray-900">
                  <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                    0 L
                  </span>
                </p>
              </div>
            )}
            <div className="rounded-lg border p-3 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-full bg-[#FFC72733]/20 p-2">
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.0625 2.12491C1.0625 0.952838 2.01543 -9.15527e-05 3.1875 -9.15527e-05H8.5C9.67207 -9.15527e-05 10.625 0.952838 10.625 2.12491V8.49991H10.8906C12.5043 8.49991 13.8125 9.80811 13.8125 11.4218V12.4843C13.8125 12.9259 14.1678 13.2812 14.6094 13.2812C15.051 13.2812 15.4062 12.9259 15.4062 12.4843V7.371C14.4898 7.13526 13.8125 6.30186 13.8125 5.31241V3.18741L12.75 2.12491C12.4578 1.83272 12.4578 1.3546 12.75 1.06241C13.0422 0.770221 13.5203 0.770221 13.8125 1.06241L16.3791 3.62901C16.7775 4.02745 17 4.56866 17 5.13311V5.57803V6.37491V7.43741V12.4843C17 13.8058 15.9309 14.8749 14.6094 14.8749C13.2879 14.8749 12.2188 13.8058 12.2188 12.4843V11.4218C12.2188 10.688 11.6244 10.0937 10.8906 10.0937H10.625V14.8749C11.2127 14.8749 11.6875 15.3497 11.6875 15.9374C11.6875 16.5251 11.2127 16.9999 10.625 16.9999H1.0625C0.474805 16.9999 0 16.5251 0 15.9374C0 15.3497 0.474805 14.8749 1.0625 14.8749V2.12491ZM3.1875 2.65616V5.84366C3.1875 6.13585 3.42656 6.37491 3.71875 6.37491H7.96875C8.26094 6.37491 8.5 6.13585 8.5 5.84366V2.65616C8.5 2.36397 8.26094 2.12491 7.96875 2.12491H3.71875C3.42656 2.12491 3.1875 2.36397 3.1875 2.65616Z"
                      fill="#FFC727"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Fuel Consumed
                </span>
              </div>
              <p className="text-xl font-semibold text-gray-900">
                <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                  {fuelsummary.consumed} L
                </span>
              </p>
            </div>
            <div className="rounded-lg border p-2 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-full bg-[#FFC72733]/20 p-2">
                  <svg
                    width="21"
                    height="17"
                    viewBox="0 0 21 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.33333 0H6.60625C5.61823 0 4.73594 0.648884 4.40781 1.61652L0.113021 14.2375C0.0401042 14.4576 0 14.6929 0 14.9281C0 16.0703 0.893229 17 1.99063 17H9.33333V14.5714C9.33333 13.8998 9.85469 13.3571 10.5 13.3571C11.1453 13.3571 11.6667 13.8998 11.6667 14.5714V17H19.0094C20.1104 17 21 16.0703 21 14.9281C21 14.6929 20.9599 14.4576 20.887 14.2375L16.5922 1.61652C16.2604 0.648884 15.3818 0 14.3938 0H11.6667V2.42857C11.6667 3.10022 11.1453 3.64286 10.5 3.64286C9.85469 3.64286 9.33333 3.10022 9.33333 2.42857V0ZM11.6667 7.28571V9.71429C11.6667 10.3859 11.1453 10.9286 10.5 10.9286Cnan nan 9.33333 10.3859 9.33333 9.71429V7.28571C9.33333 6.61406 9.85469 6.07143 10.5 6.07143Cnan nan 11.6667 6.61406 11.6667 7.28571Z"
                      fill="#FFC727"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-600">
                  Total Distance
                </span>
              </div>
              <p className="text-xl font-semibold text-gray-900">
                <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                  {fuelsummary.totalDistance} KM
                </span>
              </p>
            </div>
          </div>
          <div className="col-span-8 rounded-lg border bg-white p-2 shadow-sm">
            <Chart
              key={`${chartKey}`}
              options={chartoption}
              series={chartseries}
              type="bar"
              height={225}
            />
          </div>
        </div>
        <div className="mt-2 flex justify-start">
          <InlineFilter filtertype={3} handleGroupChange={handleGroupChange} />
        </div>
        <NeoAGgrid key={`${dataKey}`} table={tableConfigData?.table} />
      </div>
      {loadingContext && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <LoadingOverlay />
        </div>
      )}
    </div>
  );
};
